import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { apiSlice } from "../apiSlice";
import { dateFormatter } from "../../utils/dateFormatter";

const blogListAdapter = createEntityAdapter({
  selectId: (blog) => blog.id,
});

const initialState = blogListAdapter.getInitialState();

export const blogListSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getBlogList: builder.query({
      query: (filters = {}) => {
        const params = [];

        // ⚠ "0" should still count as valid
        if (filters.status !== null && filters.status !== undefined && filters.status !== "") {
          params.push(`status=${filters.status}`);
        }

        if (filters.search) {
          params.push(`search=${encodeURIComponent(filters.search)}`);
        }

        return params.length ? `blogs?${params.join("&")}` : "blogs";
      },

      transformResponse: (response) => {
        const normalized = (response?.data ?? []).map((item) => ({
          ...item,

          modifiedTitle: item.title?.toUpperCase() ?? "",
          modifiedStatus: item.status?.toUpperCase() ?? "",

          publishing_date:
            item.publishing_date !== null &&
            item.publishing_date !== undefined
              ? dateFormatter(item.publishing_date)
              : "N/A",
        }));

        return blogListAdapter.setAll(initialState, normalized);
      },

      providesTags: (result) =>
        result?.ids
          ? [
              { type: "Blog", id: "LIST" },
              ...result.ids.map((id) => ({ type: "Blog", id })),
            ]
          : [{ type: "Blog", id: "LIST" }],
    }),

    getBlogById: builder.query({
      query: (id) => `blogs/${id}`,

      transformResponse: (response) => {
        const blog = response?.data;

        if (!blog) return null;

        return {
          ...blog,
          publishing_date:
            blog.publishing_date !== null &&
            blog.publishing_date !== undefined
              ? dateFormatter(blog.publishing_date)
              : "N/A",
        };
      },

      providesTags: (result, error, id) => [{ type: "Blog", id }],
    }),
  }),
});

export const {
  useGetBlogListQuery,
  useGetBlogByIdQuery,
} = blogListSlice;

const blogFilterSelector = (state) => state.blogFilter;

const selectBlogListQueryResult = createSelector(
  [blogFilterSelector, (state) => state],
  (filters, state) =>
    blogListSlice.endpoints.getBlogList.select(filters)(state)
);

const selectBlogListData = createSelector(
  [selectBlogListQueryResult],
  (queryResult) => queryResult?.data ?? initialState
);

export const {
  selectAll: selectAllBlogs,
  selectById: selectBlogById,
  selectIds: selectBlogIds,
} = blogListAdapter.getSelectors((state) => selectBlogListData(state));
